import { useState, useEffect, useRef, useCallback } from "react";

// ── pdf.js CDN loader ────────────────────────────────────────────────
const PDFJS_VER = "3.11.174";
const PDFJS_CDN = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}`;

let _loadPromise: Promise<any> | null = null;
export function loadPdfJs(): Promise<any> {
  if (_loadPromise) return _loadPromise;
  _loadPromise = new Promise<any>((resolve, reject) => {
    const w = window as any;
    if (w.pdfjsLib) { resolve(w.pdfjsLib); return; }
    const s = document.createElement("script");
    s.src = `${PDFJS_CDN}/pdf.min.js`;
    s.onload = () => {
      const lib = w.pdfjsLib;
      if (!lib) { _loadPromise = null; reject(new Error("pdfjsLib not found")); return; }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
      resolve(lib);
    };
    s.onerror = () => { _loadPromise = null; reject(new Error("Failed to load pdf.js CDN")); };
    document.head.appendChild(s);
  });
  return _loadPromise;
}

// ── Types ────────────────────────────────────────────────────────────
interface PdfCanvasProps {
  /** PDF file or URL string to render */
  src: File | string;
  /** 0-indexed page number */
  pageNumber: number;
  /** Called when document loads with total page count */
  onPageCount?: (count: number) => void;
  /** Called if pdf.js fails to load (parent can show iframe fallback) */
  onError?: () => void;
  /** Additional CSS class */
  className?: string;
  /** Additional styles on container */
  style?: React.CSSProperties;
  /** Zoom multiplier over fit-to-width; the page is re-rendered at this resolution (default 1) */
  zoom?: number;
  /**
   * Overlay drawn on top of the page (form fields, signature boxes). It is sized to the rendered page,
   * so percentage positions stay right at any zoom; the parent scroll area scrolls sideways when zoomed.
   */
  children?: React.ReactNode;
}

// ── Component ────────────────────────────────────────────────────────
export function PdfCanvas({ src, pageNumber, onPageCount, onError, className, style, zoom = 1, children }: PdfCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);

  const renderPage = useCallback(async (doc: any, pageIdx: number) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    try {
      const page = await doc.getPage(pageIdx + 1);
      const cs = window.getComputedStyle(container);
      // Measure the frame with the canvas collapsed: a container that grows with its content (grid/flex items default
      // to min-width:auto, e.g. inside a dialog) would otherwise feed the previous zoomed width into the next render.
      const previousWidth = canvas.style.width;
      canvas.style.width = "0px";
      const containerWidth = Math.max(100, (container.clientWidth || 500) - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0"));
      canvas.style.width = previousWidth;
      // Normaliza o /Rotate intrínseco da página para que páginas giradas
      // (90/180/270) sempre rendam em pé, independente do build do pdf.js.
      const rotation = (((page.rotate ?? 0) % 360) + 360) % 360;
      const unscaled = page.getViewport({ scale: 1, rotation });
      const scale = (containerWidth / unscaled.width) * Math.max(0.25, zoom);
      const viewport = page.getViewport({ scale, rotation });

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const ctx = canvas.getContext("2d")!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      await page.render({ canvasContext: ctx, viewport }).promise;
      setPageSize({ width: viewport.width, height: viewport.height });
      setLoading(false);
    } catch (err) {
      console.error("PdfCanvas: render error", err);
    }
  }, [zoom]);

  // Load PDF
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    (async () => {
      try {
        const lib = await loadPdfJs();
        let data: any;
        if (src instanceof File) {
          data = { data: new Uint8Array(await src.arrayBuffer()) };
        } else {
          data = { url: src };
        }
        const doc = await lib.getDocument(data).promise;
        if (cancelled) return;
        pdfRef.current = doc;
        onPageCount?.(doc.numPages);
        renderPage(doc, pageNumber);
      } catch (err) {
        console.error("PdfCanvas: load error", err);
        if (!cancelled) { setFailed(true); setLoading(false); onError?.(); }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Re-render on page change
  useEffect(() => {
    if (pdfRef.current) renderPage(pdfRef.current, pageNumber);
  }, [pageNumber, renderPage]);

  // Re-render on resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pdfRef.current) return;
    const ro = new ResizeObserver(() => {
      if (pdfRef.current) renderPage(pdfRef.current, pageNumber);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [pageNumber, loading, renderPage]);

  if (failed) return null;

  return (
    <div ref={containerRef} className={className}
      style={{ width: "100%", position: "relative", background: "#fff", minHeight: 200, ...style }}>
      {loading && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 13, zIndex: 5 }}>
          ⏳ Loading PDF...
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: "block", width: "100%" }} />
      {children && pageSize && (
        <div style={{ position: "absolute", left: 0, top: 0, width: pageSize.width, height: pageSize.height, zIndex: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}
